import type { StandardEquipmentCatalogue, RecommendationOptionsInput } from "../../configurator";
import type { SavedCoilConfiguration, MeasurementRecord, ConfigurationModalFormValues } from "../../web-app";
import type { GlossaryEntry } from "../../glossary";
import type { CoilTab } from "./ui/reducer";
import type { ConfigView } from "./configurations/reducer";
import {
  HYDRATE_CATALOGUE,
  UPDATE_CATALOGUE_DRAFT,
  REVERT_CATALOGUE_DRAFT,
  SAVE_CATALOGUE_SUCCESS,
  HYDRATE_ASSUMPTIONS,
  UPDATE_ASSUMPTIONS_DRAFT,
  REVERT_ASSUMPTIONS_DRAFT,
  SAVE_ASSUMPTIONS_SUCCESS,
  HYDRATE_CONFIGURATIONS,
  SET_CONFIG_SEARCH,
  SET_CONFIG_STATUS_FILTER,
  SET_CONFIG_PAGE,
  SET_SELECTED_CONFIG_ID,
  SET_CONFIG_VIEW,
  OPEN_CONFIG_DETAIL,
  CLOSE_CONFIG_DETAIL,
  UPSERT_CONFIGURATION,
  DELETE_CONFIGURATION,
  OPEN_CONFIG_MODAL,
  CLOSE_CONFIG_MODAL,
  SUBMIT_CONFIG_MODAL,
  CLEAR_CONFIG_MODAL_ERRORS,
  RECALCULATE_CONFIGURATION,
  HYDRATE_MEASUREMENTS,
  UPSERT_MEASUREMENT,
  DELETE_MEASUREMENT,
  LOAD_APPLICATION_STATE,
  SAVE_CATALOGUE,
  SAVE_ASSUMPTIONS,
  SAVE_APPLICATION_STATE_SUCCESS,
  SET_ACTIVE_TAB,
  SET_GLOSSARY_OPEN,
  HYDRATE_GLOSSARY,
  SAVE_GLOSSARY_ENTRY,
  SAVE_GLOSSARY_ENTRY_SUCCESS,
  SHOW_TOAST,
  CLEAR_TOAST,
} from "./types";

export const loadApplicationState = () => ({ type: LOAD_APPLICATION_STATE as typeof LOAD_APPLICATION_STATE });
export const saveCatalogue = () => ({ type: SAVE_CATALOGUE as typeof SAVE_CATALOGUE });
export const saveAssumptions = () => ({ type: SAVE_ASSUMPTIONS as typeof SAVE_ASSUMPTIONS });

export const hydrateCatalogue = (catalogue: StandardEquipmentCatalogue) => ({
  type: HYDRATE_CATALOGUE as typeof HYDRATE_CATALOGUE,
  catalogue,
});
export const updateCatalogueDraft = (catalogue: StandardEquipmentCatalogue) => ({
  type: UPDATE_CATALOGUE_DRAFT as typeof UPDATE_CATALOGUE_DRAFT,
  catalogue,
});
export const revertCatalogueDraft = () => ({
  type: REVERT_CATALOGUE_DRAFT as typeof REVERT_CATALOGUE_DRAFT,
});
export const saveCatalogueSuccess = (catalogue: StandardEquipmentCatalogue) => ({
  type: SAVE_CATALOGUE_SUCCESS as typeof SAVE_CATALOGUE_SUCCESS,
  catalogue,
});

export const hydrateAssumptions = (assumptions: RecommendationOptionsInput) => ({
  type: HYDRATE_ASSUMPTIONS as typeof HYDRATE_ASSUMPTIONS,
  assumptions,
});
export const updateAssumptionsDraft = (assumptions: RecommendationOptionsInput) => ({
  type: UPDATE_ASSUMPTIONS_DRAFT as typeof UPDATE_ASSUMPTIONS_DRAFT,
  assumptions,
});
export const revertAssumptionsDraft = () => ({
  type: REVERT_ASSUMPTIONS_DRAFT as typeof REVERT_ASSUMPTIONS_DRAFT,
});
export const saveAssumptionsSuccess = (assumptions: RecommendationOptionsInput) => ({
  type: SAVE_ASSUMPTIONS_SUCCESS as typeof SAVE_ASSUMPTIONS_SUCCESS,
  assumptions,
});

export const hydrateConfigurations = (items: SavedCoilConfiguration[]) => ({
  type: HYDRATE_CONFIGURATIONS as typeof HYDRATE_CONFIGURATIONS,
  items,
});
export const setConfigSearch = (search: string) => ({
  type: SET_CONFIG_SEARCH as typeof SET_CONFIG_SEARCH,
  search,
});
export const setConfigStatusFilter = (statusFilter: string[]) => ({
  type: SET_CONFIG_STATUS_FILTER as typeof SET_CONFIG_STATUS_FILTER,
  statusFilter,
});
export const setConfigPage = (page: number) => ({
  type: SET_CONFIG_PAGE as typeof SET_CONFIG_PAGE,
  page,
});
export const setSelectedConfigId = (selectedId: string | null) => ({
  type: SET_SELECTED_CONFIG_ID as typeof SET_SELECTED_CONFIG_ID,
  selectedId,
});
export const setConfigView = (view: ConfigView) => ({
  type: SET_CONFIG_VIEW as typeof SET_CONFIG_VIEW,
  view,
});

export const openConfigurationDetail = (id: string) => ({
  type: OPEN_CONFIG_DETAIL as typeof OPEN_CONFIG_DETAIL,
  id,
});

export const closeConfigurationDetail = () => ({
  type: CLOSE_CONFIG_DETAIL as typeof CLOSE_CONFIG_DETAIL,
});

export const upsertConfiguration = (config: SavedCoilConfiguration) => ({
  type: UPSERT_CONFIGURATION as typeof UPSERT_CONFIGURATION,
  config,
});

export const deleteConfiguration = (id: string) => ({
  type: DELETE_CONFIGURATION as typeof DELETE_CONFIGURATION,
  id,
});

export const openConfigModal = (mode: "create" | "edit", editId?: string) => ({
  type: OPEN_CONFIG_MODAL as typeof OPEN_CONFIG_MODAL,
  mode,
  editId: editId ?? null,
});

export const closeConfigModal = () => ({
  type: CLOSE_CONFIG_MODAL as typeof CLOSE_CONFIG_MODAL,
});

export const submitConfigModal = (values: ConfigurationModalFormValues) => ({
  type: SUBMIT_CONFIG_MODAL as typeof SUBMIT_CONFIG_MODAL,
  values,
});

export const clearConfigModalErrors = () => ({
  type: CLEAR_CONFIG_MODAL_ERRORS as typeof CLEAR_CONFIG_MODAL_ERRORS,
});

export const recalculateConfiguration = (id: string) => ({
  type: RECALCULATE_CONFIGURATION as typeof RECALCULATE_CONFIGURATION,
  id,
});

export const hydrateMeasurements = (records: MeasurementRecord[]) => ({
  type: HYDRATE_MEASUREMENTS as typeof HYDRATE_MEASUREMENTS,
  records,
});

export const upsertMeasurement = (
  record: MeasurementRecord,
  options?: { fromModal?: boolean },
) => ({
  type: UPSERT_MEASUREMENT as typeof UPSERT_MEASUREMENT,
  record,
  fromModal: options?.fromModal ?? false,
});

export const deleteMeasurement = (id: string) => ({
  type: DELETE_MEASUREMENT as typeof DELETE_MEASUREMENT,
  id,
});

export const setActiveTab = (tab: CoilTab) => ({
  type: SET_ACTIVE_TAB as typeof SET_ACTIVE_TAB,
  tab,
});
export const setGlossaryOpen = (open: boolean) => ({
  type: SET_GLOSSARY_OPEN as typeof SET_GLOSSARY_OPEN,
  open,
});
export const hydrateGlossary = (entries: Record<string, GlossaryEntry>) => ({
  type: HYDRATE_GLOSSARY as typeof HYDRATE_GLOSSARY,
  entries,
});
export const saveGlossaryEntry = (entryKey: string, entry: GlossaryEntry) => ({
  type: SAVE_GLOSSARY_ENTRY as typeof SAVE_GLOSSARY_ENTRY,
  entryKey,
  entry,
});
export const saveGlossaryEntrySuccess = (entryKey: string, entry: GlossaryEntry) => ({
  type: SAVE_GLOSSARY_ENTRY_SUCCESS as typeof SAVE_GLOSSARY_ENTRY_SUCCESS,
  entryKey,
  entry,
});
export const showToast = (message: string, toastType: "success" | "error" = "success") => ({
  type: SHOW_TOAST as typeof SHOW_TOAST,
  message,
  toastType,
});
export const clearToast = () => ({ type: CLEAR_TOAST as typeof CLEAR_TOAST });
